import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar, MapPin, Users, Phone, Mail } from 'lucide-react';
import axios from 'axios';
import { TravelBookingCard } from '../components/TravelBookingCard';
import { PayButton, VoucherButton } from '../components/PaymentButtons';
import { api } from '../api';
import { Plane } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MyBookings = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [travelBookings, setTravelBookings] = useState([]);
  const [prices, setPrices] = useState({});
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(null);

  useEffect(() => {
    checkAuthAndFetchBookings();
  }, []);

  const checkAuthAndFetchBookings = async () => {
    try {
      // Check authentication
      await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true
      });
      setIsAuthenticated(true);
      
      // Fetch bookings
      const [response, travel] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/bookings`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/travel/bookings`, { withCredentials: true }),
      ]);
      setBookings(response.data);
      setTravelBookings(travel.data);
      const [d, p] = await Promise.all([api.get('/destinations'), api.get('/packages')]);
      const map = {};
      d.data.forEach((x) => { map[`d:${x.name}`] = x.price; });
      p.data.forEach((x) => { map[`p:${x.name}`] = x.price; });
      setPrices(map);
    } catch (error) {
      console.error('Error:', error);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center pt-16">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
            <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
          </div>
          <p className="text-gray-600 text-lg">Loading your bookings...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated === false) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center pt-16" data-testid="my-bookings-login-required">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Please Login</h2>
          <p className="text-gray-600 mb-6">You need to be logged in to view your bookings</p>
          <Button
            data-testid="my-bookings-login-btn"
            onClick={() => navigate('/login')}
            className="bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white"
          >
            Login Now
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-16" data-testid="my-bookings-page">
      <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold mb-2">My Bookings</h1>
          <p className="text-teal-50 text-lg">Manage and track all your travel bookings</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-12">
        <section data-testid="travel-bookings-section">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center"><Plane className="w-6 h-6 mr-2 text-teal-600" />Flights & Hotels</h2>
          {travelBookings.length === 0 ? (
            <Card className="border-0 shadow-sm"><CardContent className="p-8 text-center text-gray-500" data-testid="travel-bookings-empty">
              No flight or hotel reservations yet.
              <div className="mt-4 flex justify-center gap-3">
                <Button variant="outline" onClick={() => navigate('/flights')} className="border-2 border-teal-500 text-teal-600">Search flights</Button>
                <Button variant="outline" onClick={() => navigate('/hotels')} className="border-2 border-teal-500 text-teal-600">Find hotels</Button>
              </div>
            </CardContent></Card>
          ) : (
            <div className="space-y-4">{travelBookings.map((b) => <TravelBookingCard key={b.booking_id} b={b} />)}</div>
          )}
        </section>

        <h2 className="text-2xl font-bold text-gray-900 -mb-8">Packages & Destinations</h2>
        {bookings.length === 0 ? (
          <Card className="border-0 shadow-lg">
            <CardContent className="p-12 text-center">
              <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">No Bookings Yet</h3>
              <p className="text-gray-600 mb-6">Start planning your next adventure!</p>
              <Button
                onClick={() => navigate('/destinations')}
                className="bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white"
              >
                Explore Destinations
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6" data-testid="my-bookings-list">
            {bookings.map((booking) => (
              <Card key={booking.booking_id} data-testid={`booking-card-${booking.booking_id}`} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardHeader className="bg-gradient-to-r from-teal-50 to-cyan-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-2xl">{booking.destination}</CardTitle>
                      {booking.package_name && <p className="text-sm text-teal-700 mt-1">{booking.package_name}</p>}
                    </div>
                    <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
                      booking.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                      booking.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                      booking.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {booking.status?.toUpperCase()}
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3">
                        <Calendar className="w-5 h-5 text-teal-600" />
                        <div>
                          <p className="text-sm text-gray-600">Travel Date</p>
                          <p className="font-semibold">{booking.travel_date}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Users className="w-5 h-5 text-teal-600" />
                        <div>
                          <p className="text-sm text-gray-600">Travelers</p>
                          <p className="font-semibold">{booking.travelers} Person(s)</p>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3">
                        <Mail className="w-5 h-5 text-teal-600" />
                        <div>
                          <p className="text-sm text-gray-600">Email</p>
                          <p className="font-semibold">{booking.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Phone className="w-5 h-5 text-teal-600" />
                        <div>
                          <p className="text-sm text-gray-600">Phone</p>
                          <p className="font-semibold">{booking.phone}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  {booking.special_requests && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Special Requests:</p>
                      <p className="text-gray-900">{booking.special_requests}</p>
                    </div>
                  )}
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <p className="text-sm text-gray-600">
                      Booking ID: <span className="font-mono font-semibold">{booking.booking_id}</span>
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      Booked on: {new Date(booking.created_at).toLocaleDateString()}
                    </p>
                    <div className="flex flex-wrap items-center gap-2 mt-4" data-testid={`booking-actions-${booking.booking_id}`}>
                      <VoucherButton kind="package" bookingId={booking.booking_id} />
                      {booking.payment_status === 'paid' ? (
                        <span className="text-sm font-semibold text-green-600" data-testid={`booking-paid-${booking.booking_id}`}>Paid ✓</span>
                      ) : booking.status !== 'cancelled' && (prices[`p:${booking.package_name}`] || prices[`d:${booking.destination}`]) ? (
                        <PayButton kind="package" bookingId={booking.booking_id} amountInr={(prices[`p:${booking.package_name}`] || prices[`d:${booking.destination}`]) * booking.travelers} />
                      ) : null}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyBookings;
